// The pure half of `/auth/callback` (routes/AuthCallbackPage.tsx), apart so that
// `authCallback.test.ts` can run it under Node (app#377, the app half of router#1768).

export type SessionTokens = {
  access_token: string
  expires_in: number
  token_type?: string
}

export interface ParsedFragment {
  tokens: SessionTokens | null
  nextPath: string
  authMethod: string | null
}

// `next` is a path in THIS app, or it is `/`. A value that starts with `//` or `/\`
// is a URL on another host: the browser reads `//evil.example` as
// `https://evil.example`, and it reads `\` as `/`. A control character is refused
// too, because the browser removes a tab or a newline from a URL, and `/\t/evil`
// then becomes `//evil`.
//
// A dot segment can make `//` too: the browser reads `/..//evil`, `/.//evil`,
// `/%2e//evil` and `/./\evil` as the path `//evil`. On that path, React Router
// resolves a relative link to `//evil/…`, and its `push` then goes to the other
// host. So the path is resolved here, and the result is used only when it does not
// start with `//`.
const NEXT_BASE = 'https://app.invalid'

export const safeNextPath = (next: string | null): string => {
  if (!next || !/^\/(?![/\\])/.test(next)) return '/'
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(next)) return '/'
  let url: URL
  try {
    url = new URL(next, NEXT_BASE)
  } catch {
    return '/'
  }
  if (url.origin !== NEXT_BASE || url.pathname.startsWith('//')) return '/'
  return `${url.pathname}${url.search}${url.hash}`
}

// The router puts the access token in the fragment. It does not send a
// `refresh_token` there (router#1768): the HttpOnly cookie holds it, and the app
// never reads it.
export const parseAuthFragment = (locationSearch: string, locationHash: string): ParsedFragment => {
  const nextPath = safeNextPath(new URLSearchParams(locationSearch).get('next'))
  const hash = locationHash.startsWith('#') ? locationHash.slice(1) : locationHash

  if (!hash) {
    return { tokens: null, nextPath, authMethod: null }
  }

  const params = new URLSearchParams(hash)
  const accessToken = params.get('access_token')
  const authMethod = params.get('auth_method')

  if (!accessToken) {
    return { tokens: null, nextPath, authMethod }
  }

  const expiresIn = Number.parseInt(params.get('expires_in') ?? '3600', 10)

  return {
    nextPath,
    authMethod,
    tokens: {
      access_token: accessToken,
      expires_in: Number.isFinite(expiresIn) ? expiresIn : 3600,
      token_type: params.get('token_type') ?? undefined,
    },
  }
}

export interface CallbackSteps {
  // Removes the fragment from the address bar and from the history entry.
  clearAuthParams: () => void
  // Keeps the token in memory. False when the user identity cookie is missing.
  setAccessToken: (token: string) => boolean
  // Called after the token is kept, with how the router authenticated the user.
  remember: (authMethod: string | null) => void
}

// Finishes a sign-in and gives the path to go to. It clears the fragment FIRST,
// before any step that can throw: on the error path the access token must not stay
// in the address bar or in the history (router#1768, RP-22).
export const finalizeCallback = (search: string, hash: string, steps: CallbackSteps): string => {
  steps.clearAuthParams()
  const { tokens, nextPath, authMethod } = parseAuthFragment(search, hash)

  if (!tokens) {
    throw new Error('Missing session tokens in callback hash')
  }

  if (!steps.setAccessToken(tokens.access_token)) {
    throw new Error('Authentication incomplete - missing user identity cookie. Please try signing in again.')
  }

  steps.remember(authMethod)
  return nextPath
}
